import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { analytics } from '../services/analytics';
import {
  ANALYTICS_CONSENT_EVENT,
  getAnalyticsConsent,
  type AnalyticsConsent,
} from '../services/analyticsConsent';
import { borderRadius, breakpoints, colors, shadows, spacing, typography, zIndex } from '../styles/theme';

const Banner = styled.aside`
  position: fixed;
  z-index: ${zIndex.banner};
  right: ${spacing[4]};
  bottom: ${spacing[4]};
  left: ${spacing[4]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[4]};
  max-width: 880px;
  margin: 0 auto;
  padding: ${spacing[4]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.lg};
  background: ${colors.white};
  box-shadow: ${shadows.lg};

  @media (max-width: ${breakpoints.sm}) {
    right: ${spacing[3]};
    bottom: ${spacing[3]};
    left: ${spacing[3]};
    align-items: stretch;
    flex-direction: column;
  }
`;

const Copy = styled.div`
  min-width: 0;

  strong {
    display: block;
    margin-bottom: ${spacing[1]};
    color: ${colors.text.primary};
    font-size: ${typography.fontSizes.base};
  }

  p {
    margin: 0;
    color: ${colors.text.secondary};
    font-size: ${typography.fontSizes.sm};
    line-height: ${typography.lineHeights.relaxed};
  }

  a {
    color: ${colors.text.link};
  }
`;

const Actions = styled.div`
  display: flex;
  flex: 0 0 auto;
  gap: ${spacing[2]};

  @media (max-width: ${breakpoints.sm}) {
    > button { flex: 1; }
  }
`;

const ChoiceButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'primary',
})<{ primary?: boolean }>`
  min-height: 44px;
  padding: 0 ${spacing[4]};
  border: 1px solid ${(props) => props.primary ? colors.primary[600] : colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${(props) => props.primary ? colors.primary[600] : colors.white};
  color: ${(props) => props.primary ? colors.white : colors.text.primary};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;

  &:hover { filter: brightness(0.97); }
  &:focus-visible { outline: 2px solid ${colors.border.focus}; outline-offset: 2px; }
`;

const Settings = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  padding: ${spacing[4]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.lg};
  background: ${colors.background.secondary};
`;

const Status = styled.span`
  margin-right: auto;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
`;

function useConsent(): AnalyticsConsent | null {
  const [consent, setConsent] = useState<AnalyticsConsent | null>(() => getAnalyticsConsent());
  useEffect(() => {
    const onChange = (event: Event) => setConsent((event as CustomEvent<AnalyticsConsent>).detail);
    window.addEventListener(ANALYTICS_CONSENT_EVENT, onChange);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, onChange);
  }, []);
  return consent;
}

export const AnalyticsConsentBanner: React.FC = () => {
  const consent = useConsent();
  if (consent) return null;

  return (
    <Banner role="dialog" aria-labelledby="analytics-consent-title" aria-describedby="analytics-consent-description">
      <Copy>
        <strong id="analytics-consent-title">Help improve Event Finder</strong>
        <p id="analytics-consent-description">
          Allow analytics cookies for usage measurement and Clarity session diagnostics. You can continue without them.{' '}
          <Link to="/cookies">Cookie Policy</Link>
        </p>
      </Copy>
      <Actions>
        <ChoiceButton type="button" onClick={() => analytics.setAnalyticsConsent(false)}>
          Continue without
        </ChoiceButton>
        <ChoiceButton type="button" primary onClick={() => analytics.setAnalyticsConsent(true)}>
          Allow analytics
        </ChoiceButton>
      </Actions>
    </Banner>
  );
};

export const AnalyticsConsentSettings: React.FC = () => {
  const consent = useConsent();
  return (
    <Settings aria-label="Analytics cookie settings">
      <Status>
        Analytics cookies: <strong>{consent === 'granted' ? 'Allowed' : consent === 'denied' ? 'Not allowed' : 'No choice saved'}</strong>
      </Status>
      <ChoiceButton type="button" onClick={() => analytics.setAnalyticsConsent(false)}>
        Do not allow
      </ChoiceButton>
      <ChoiceButton type="button" primary onClick={() => analytics.setAnalyticsConsent(true)}>
        Allow analytics
      </ChoiceButton>
    </Settings>
  );
};

export default AnalyticsConsentBanner;